import { buildLoginUrl, config } from "./config";

const TOKEN_KEYS = ["id_token", "access_token"] as const;
const STATE_KEY = "oauth_state";

type IdTokenPayload = {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  exp?: number;
};

function generateState(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function storeState(value: string) {
  try {
    sessionStorage.setItem(STATE_KEY, value);
  } catch {
    // Ignore storage failures; state validation will be skipped.
  }
}

function readState(): string | null {
  try {
    return sessionStorage.getItem(STATE_KEY);
  } catch {
    return null;
  }
}

function clearState() {
  try {
    sessionStorage.removeItem(STATE_KEY);
  } catch {
    // Ignore storage failures so login can still proceed.
  }
}

function storeToken(key: (typeof TOKEN_KEYS)[number], value: string) {
  localStorage.setItem(key, value);
}

function clearTokens() {
  TOKEN_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });
  localStorage.removeItem("auth_debug");
}

function decodeJwtPayload(token: string | null): IdTokenPayload | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const padded = parts[1].padEnd(Math.ceil(parts[1].length / 4) * 4, "=");
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as IdTokenPayload;
  } catch {
    return null;
  }
}

function decodeIdToken(): IdTokenPayload | null {
  return decodeJwtPayload(localStorage.getItem("id_token"));
}

export function isAuthenticated(): boolean {
  if (typeof window === "undefined") return false;

  const token = localStorage.getItem("id_token") ?? localStorage.getItem("access_token");
  if (!token) return false;

  const payload = decodeJwtPayload(token);
  if (payload?.exp) {
    return payload.exp * 1000 > Date.now();
  }

  return true;
}

export function getUserDisplayName(): string | null {
  const payload = decodeIdToken();
  if (!payload) return null;
  const full = payload.name || [payload.given_name, payload.family_name].filter(Boolean).join(" ").trim();
  return full || payload.given_name || payload.family_name || payload.email || null;
}

export function startLogin(): void {
  if (typeof window === "undefined") return;

  const state = generateState();
  storeState(state);
  window.location.href = buildLoginUrl(state);
}

export function startLogout(): void {
  if (typeof window === "undefined") return;

  clearTokens();
  const logoutUrl = `${config.cognitoDomain}/logout?client_id=${encodeURIComponent(
    config.userPoolClientId
  )}&logout_uri=${encodeURIComponent(config.redirectUri)}`;
  window.location.href = logoutUrl;
}

export function persistTokensFromHash(): void {
  if (typeof window === "undefined") return;

  const params = readAuthParams();
  if (!params) return;

  // Best-effort state cleanup; do not block token storage if it differs or is missing.
  clearState();
  // Debug marker to verify parsing ran at all.
  localStorage.setItem("auth_debug", params.toString());
  let stored = false;

  TOKEN_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      storeToken(key, value);
      stored = true;
    }
  });

  if (!stored) return;

  if (window.history.replaceState) {
    const baseUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, "", baseUrl);
  }
}

function readAuthParams(): URLSearchParams | null {
  const hash = window.location.hash;
  if (hash && hash !== "#") {
    return new URLSearchParams(hash.replace(/^#/, ""));
  }

  // Some environments may rewrite the fragment into the query string; handle that too.
  const search = window.location.search;
  if (search) {
    return new URLSearchParams(search.replace(/^\?/, ""));
  }

  return null;
}